import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, Loader2, Trash2, FileText, ImageIcon, X } from "lucide-react";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];

/**
 * FileUploader — attaches to a trade or a payment, tagged by `kind`.
 * Props: kind ('photo'|'invoice'|'receipt'), tradeId?, paymentId?, label, accept, refreshKey, onChange
 */
export function FileUploader({ kind, tradeId, paymentId, label, accept, onChange }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {url, filename}
  const [token, setToken] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setToken(data?.session?.access_token || ""));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setToken(s?.access_token || ""));
    return () => sub.subscription.unsubscribe();
  }, []);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ kind });
    if (tradeId) params.set("trade_id", tradeId);
    if (paymentId) params.set("payment_id", paymentId);
    try {
      const { data } = await api.get(`/attachments?${params.toString()}`);
      setFiles(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to load attachments:", e);
    }
  }, [kind, tradeId, paymentId]);

  useEffect(() => { load(); }, [load]);

  async function onPick(e) {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    setBusy(true);
    let uploaded = 0;
    try {
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        if (tradeId) fd.append("trade_id", tradeId);
        if (paymentId) fd.append("payment_id", paymentId);
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded++;
      }
      toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} uploaded`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("File removed");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  function openPreview(f) {
    const url = `${API_BASE}/attachments/${f.id}/download?auth=${encodeURIComponent(token || "")}`;
    setPreview({ url, filename: f.original_filename, contentType: f.content_type });
  }

  const testId = `uploader-${kind}${tradeId ? `-trade-${tradeId}` : ""}${paymentId ? `-payment-${paymentId}` : ""}`;

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-10 bg-brandblue hover:bg-brandblue-hover text-white"
          data-testid={`btn-${testId}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          {busy ? "Uploading…" : `Upload ${kind}${kind === "photo" ? "s" : ""}`}
        </Button>
        <input
          type="file"
          ref={inputRef}
          onChange={onPick}
          multiple={kind === "photo"}
          accept={accept}
          className="hidden"
          data-testid={`input-${testId}`}
        />
      </div>

      {files.length === 0 ? (
        <div className="text-sm text-slate-400 py-4 px-4 border border-dashed border-border rounded-md text-center">
          No {kind}s uploaded yet.
        </div>
      ) : (
        <div className={kind === "photo" ? "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" : "space-y-2"}>
          {files.map((f) => (
            <AttachmentTile key={f.id} f={f} kind={kind} token={token} onOpen={() => openPreview(f)} onRemove={() => remove(f.id)} />
          ))}
        </div>
      )}

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function AttachmentTile({ f, kind, onOpen, onRemove, token }) {
  const src = `${API_BASE}/attachments/${f.id}/download?auth=${encodeURIComponent(token || "")}`;
  const isImage = IMAGE_TYPES.includes(f.content_type);

  if (kind === "photo" && isImage) {
    return (
      <div className="relative group aspect-square rounded-md overflow-hidden border border-border bg-slate-100" data-testid={`attachment-${f.id}`}>
        <img src={src} alt={f.original_filename} className="w-full h-full object-cover cursor-pointer" onClick={onOpen} />
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 h-7 w-7 rounded-md bg-white/90 hover:bg-status-danger-bg text-slate-600 hover:text-status-danger-text opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          data-testid={`btn-remove-attachment-${f.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 border border-border rounded-md hover:bg-slate-50 transition-colors" data-testid={`attachment-${f.id}`}>
      <div className="h-10 w-10 rounded-md bg-status-neutral-bg text-status-neutral-text flex items-center justify-center shrink-0">
        {isImage ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </div>
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className="text-sm font-medium text-slate-800 truncate hover:text-brandblue">{f.original_filename}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{(f.size / 1024).toFixed(0)} KB · {f.content_type?.split("/")[1]}</div>
      </button>
      <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8 text-slate-400 hover:text-status-danger-text" data-testid={`btn-remove-attachment-${f.id}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  const isImage = preview.contentType?.startsWith("image/");
  const isPdf = preview.contentType === "application/pdf";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="attachment-preview">
      <button className="absolute top-4 right-4 h-10 w-10 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" onClick={onClose} data-testid="btn-close-preview">
        <X className="h-5 w-5" />
      </button>
      <div className="max-w-5xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {isImage ? (
          <img src={preview.url} alt={preview.filename} className="w-full h-full object-contain max-h-[90vh] rounded-md" />
        ) : isPdf ? (
          <iframe src={preview.url} title={preview.filename} className="w-full h-[85vh] rounded-md bg-white" />
        ) : (
          <div className="bg-white rounded-md p-8 text-center">
            <p className="mb-4">{preview.filename}</p>
            <a href={preview.url} download={preview.filename} className="text-brandblue font-semibold hover:underline">Download file</a>
          </div>
        )}
      </div>
    </div>
  );
}
